<script setup>
  import { ref, onMounted, computed } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useUserStore } from '@/stores/userStore'
  import { useContractStore } from '@/stores/contractStore'

  const emit = defineEmits(['account'])

  const router = useRouter()
  const route = useRoute()
  const userStore = useUserStore()
  const contractStore = useContractStore()

  // ルーターからテナントIDを取得
  const dashboardPath = '/dashboard'

  // デフォルトのアイコンURL
  const defaultIcon = new URL('@/assets/img/header_account.svg', import.meta.url).href

  const pages = [
    {
      path: '/company',
      name: '会社一覧',
    },
    {
      path: '/todo',
      name: '案件進捗',
    },
    {
      path: '/electronicapplication',
      name: '電子申請',
    },
    {
      path: '/subsidy',
      name: '助成金',
    },
    {
      path: '/article36agreement',
      name: '36協定',
    },
    {
      path: '/egovlist',
      name: 'e-Gov電子申請',
    },
  ]

  const user_menus = [
    {
      path: '/user',
      name: 'ユーザーリスト',
      isSingOut: false,
      isManual: false,
      isManager: false,
    },
    // {
    //   path: '/info',
    //   name: 'お知らせ編集',
    //   isSingOut: false,
    //   isManual: false,
    //   isManager: true,
    // },
    {
      path: '/contract',
      name: '契約確認',
      isSingOut: false,
      isManual: false,
      isManager: true,
    },
    {
      path: '/terms',
      name: '利用規約',
      isSingOut: false,
      isManual: false,
      isManager: false,
    },
    {
      path: '/signout',
      name: 'サインアウト',
      isSingOut: true,
      isManual: false,
      isManager: false,
    },
  ]
  const current_user_menus = ref([])

  // ユーザーアイコン
  const userIconSrc = computed(() => {
    return userStore.userIconUrl || defaultIcon
  })

  const showPopupMenu = ref(false)
  const closePopupMenu = () => {
    showPopupMenu.value = false
  }

  // ログアウト
  const logout = async () => {
    await userStore.logout()
    location.href = '/login'
  }

  // メニュークリック
  const onMenuClick = async menu => {
    if (menu.isSingOut) {
      await logout()
    }
  }

  // アカウント
  const account = () => {
    if (route.path !== '/user') {
      router.push({ path: '/user', query: { id: userStore.currentUser.id } })
    } else {
      // 現在のページがユーザーリストの場合、親コンポーネントにイベントを発行
      emit('account')
    }
  }

  // メニューをフィルタリングする関数
  const filterUserMenus = () => {
    current_user_menus.value = user_menus.filter(menu => {
      if (!userStore.isAdmin()) {
        return menu.isManager === false
      }
      return true
    })
  }

  // システムお知らせへの遷移
  const goToSystemInfo = () => {
    router.push({ path: '/systeminfo' })
  }

  // サブスクリプションページへの遷移
  const goToSubscription = () => {
    // rootユーザー以外はクリックできない
    if (!userStore.isRoot()) {
      return
    }
    router.push({ path: '/subscription' })
  }

  // パスが現在のルートまたはその子ルートと一致するかチェックする関数
  const isActiveRoute = path => {
    // 完全一致の場合
    if (router.currentRoute.value.path === path) {
      return true
    }

    // 子ルートの場合（パスの先頭が一致するかチェック）
    // 例: '/company'が'/company/123/branch'の親ルートとして認識される
    if (path !== dashboardPath && router.currentRoute.value.path.startsWith(path + '/')) {
      return true
    }

    return false
  }

  // 無料帯を表示するかどうか
  const showFreePlanBanner = computed(() => {
    // トライアル中、解約済み、期限切れの場合に表示
    return contractStore.isTrialStatus || contractStore.isCanceledStatus || contractStore.isExpiredStatus
  })

  // トライアル用のメッセージ構成要素
  const trialMessageParts = computed(() => {
    if (contractStore.trialDaysRemaining) {
      return {
        beforeLink: `トライアル期間中（残り${contractStore.trialDaysRemaining}日）─ ご継続は有料プランへの`,
        linkText: '登録',
        afterLink: 'をお願いします。',
      }
    }
    return {
      beforeLink: 'トライアル期間中 ─ ご継続は有料プランへの',
      linkText: '登録',
      afterLink: 'をお願いします。',
    }
  })

  // 無料プラン用のメッセージ構成要素
  const freePlanMessageParts = computed(() => {
    return {
      beforeLink: '現在は無料プランです ─ 閲覧のみ可能。データの追加・ダウンロードには有料プランへの',
      linkText: '登録',
      afterLink: 'が必要です。',
    }
  })

  onMounted(async () => {
    await userStore.getCurrentUser()
    await contractStore.getCurrentContract()

    // メニュー表示の設定
    filterUserMenus()
  })
</script>

<template>
  <!-- ヘッダー全体のコンテナ -->
  <div class="header-container">
    <header class="app-header">
      <nav>
        <ul>
          <li :class="{ active: router.currentRoute.value.path === dashboardPath }">
            <router-link :to="dashboardPath">
              <img class="deactive" src="@/assets/img/header_logo.svg" alt="ディーモ for 社労士" />
              <img class="active" src="@/assets/img/header_logo-active.svg" alt="ディーモ for 社労士" />
            </router-link>
          </li>
          <li v-for="(page, index) in pages" :key="index" :class="{ active: isActiveRoute(page.path) }">
            <router-link :to="page.path">
              {{ page.name }}
            </router-link>
          </li>
        </ul>
        <!-- 新しいボタンをユーザーアイコンの左側に追加 -->
        <button class="header-action-button" @click="goToSystemInfo">
          <div>
            <span class="material-symbols-outlined">notifications</span>
          </div>
        </button>
        <div class="user" @click.stop v-if="userStore.currentUser">
          <img class="icon" :src="userIconSrc" @click.stop="showPopupMenu = !showPopupMenu" />
          <div class="user-menu fadein" v-if="showPopupMenu" @click.stop>
            <button class="close" type="button" @click="closePopupMenu">
              <span class="material-symbols-outlined">close</span>
            </button>
            <dl>
              <!-- <dt></dt> -->
              <dd @click="account">
                <img class="icon" :src="userIconSrc" />
                <span>
                  {{ userStore.getTruncatedUserName(userStore.currentUserShortName) }}
                </span>
              </dd>
            </dl>
            <ul>
              <li v-for="(menu, index) in current_user_menus" :key="index" :class="{ signout: menu.isSingOut }" @click="onMenuClick(menu)">
                <router-link v-if="!menu.isSingOut" :to="menu.path">
                  {{ menu.name }}
                </router-link>
                <a v-else href="#">{{ menu.name }}</a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>

    <!-- 無料プラン・トライアル表示帯をヘッダーコンテナ内に配置 -->
    <!-- バナーが表示される場合 -->
    <div v-if="showFreePlanBanner" class="trial-banner" :class="{ clickable: userStore.isRoot() }" @click="goToSubscription">
      <div class="trial-content">
        <!-- トライアル中の場合 -->
        <span v-if="contractStore.isTrialStatus" class="trial-text">{{ trialMessageParts.beforeLink }}{{ trialMessageParts.linkText }}{{ trialMessageParts.afterLink }}</span>
        <!-- 無料プラン（解約済み・期限切れ）の場合 -->
        <span v-else class="trial-text">{{ freePlanMessageParts.beforeLink }}{{ freePlanMessageParts.linkText }}{{ freePlanMessageParts.afterLink }}</span>
      </div>
    </div>
    <!-- バナーが表示されない場合でもスペースを確保 -->
    <div v-else class="trial-banner-placeholder"></div>
  </div>
</template>

<style lang="less" scoped>
  .header-container {
    /* ヘッダーコンテナ全体を一つの単位として管理 */
    display: flex;
    flex-direction: column;
    /* 高さの変化をスムーズにするトランジション */
    transition: all 0.3s ease;
  }

  .trial-banner {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  .trial-banner:not(.clickable) {
    cursor: default;
  }
</style>
